import { ConnectedCloudsAndContent } from '@readcloud/clouds-and-content';
import { trackEvent, useReadCloudAuth } from '@readcloud/common';
import { Book, Cloud } from '@readcloud/data';
import {
  ABSOLUTE_PATH,
  cloudsAndContentActions,
  reportActions,
  useAppDispatch,
} from '@readcloud/state';
import axios from 'axios';
import { tauriFetch } from '@readcloud/fetch-wrapper';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { openUrl } from '@tauri-apps/plugin-opener';

export function CloudsAndContentPage({
  refreshing,
  onRefresh,
}: {
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const dispatch = useAppDispatch();
  const { logout, user } = useReadCloudAuth();
  const history = useHistory();

  const handleBookClicked = useCallback(
    async (book?: Book, cloud?: Cloud) => {
      trackEvent('Webapp/TileClicked', {
        bookId: book.id,
        isbn: book.isbn,
        bookType: book.type,
        bookTitle: book.title,
        cloudName: cloud.name,
        cloudId: cloud.id,
      });

      if (book?.id) {
        dispatch(
          reportActions.asyncActions.createUserActivity({
            type: 'BookOpen',
            bookId: book.id,
            ISBN: book.isbn,
            ID: book.id,
            title: book.title,
          })
        );
      }

      /* 
      Save the current scroll position before navigating away
      */
      dispatch(
        cloudsAndContentActions.updateScrollPosition({
          cloudId: cloud.id,
          containerPosition:
            document.getElementById('scroll-container')?.scrollTop || 0,
          cloudPosition:
            document.getElementById(cloud.id + '-inner-container')
              ?.scrollLeft || 0,
        })
      );

      /*
       * Launch interactive tile with LTI if
       * data is avaialble. Otherwise, just launch in new tab
       */

      const isTauri = !!(window as any).__TAURI__;

      // if in a Tauri environment, use Tauri's opener plugin, else use the normal browser.
      const openWindow = isTauri
        ? (url: string) => openUrl(url)
        : (url: string) => window.open(url, '_blank');

      // Tauri's open returns void, so we don't have access to the window to inject.
      // For now we'll just ignore this case if in a Tauri Env.
      if (book && !!book.intLoginData && !isTauri) {
        const w = window.open('', '_blank');
        const headers = {
          Authorization: `Bearer ${user.accessToken}`,
        };

        interface LTILaunchResponse {
          result?: {
            html?: string;
          };
        }

        const html = await axios
          .get<LTILaunchResponse>('/api/rc/v14/lti_launch_data', {
            params: { bookId: book?.id },
            headers: headers,
          })
          .then(({ data }) => {
            if (data && data.result && data.result.html) {
              return data.result.html;
            }
            return null;
          });

        if (html && !html.includes('http-equiv')) {
          if (w) {
            w.document.write(html);
          }
        } else if (book.downloadURI) {
          w.location = book.downloadURI;
        }
      } else if (isTauri && book && !!book.intLoginData) {
        /*
         * Tauri apps can't window.open + document.write, so we mirror the
         * Android native app: pre-check the backend can launch this tile,
         * then hand the system default browser a signed GET URL to the
         * /lti_launch endpoint, which renders the auto-submitting LTI form.
         */
        interface LTILaunchResponse {
          result?: {
            html?: string;
          };
        }
        try {
          // Route through tauriFetch so the request goes via the Rust HTTP
          // plugin in Tauri builds — the webview's origin (tauri://localhost
          // or https://tauri.localhost) isn't in this endpoint's CORS allowlist,
          // and Rust-side requests bypass browser CORS entirely.
          const res = await tauriFetch(
            `${ABSOLUTE_PATH}/api/rc/v14/lti_launch_data?bookId=${encodeURIComponent(
              book.id
            )}`,
            { headers: { Authorization: `Bearer ${user.accessToken}` } }
          );
          if (!res.ok) throw new Error(`LTI pre-check failed: ${res.status}`);
          const data = (await res.json()) as LTILaunchResponse;
          if (!data?.result?.html) throw new Error('LTI launch unavailable');

          const launchUrl =
            `${ABSOLUTE_PATH}/api/rc/v14/lti_launch` +
            `?auth=${encodeURIComponent(user.accessToken)}` +
            `&bookId=${encodeURIComponent(book.id)}`;
          await openUrl(launchUrl);
        } catch (err) {
          if (book.downloadURI) await openUrl(book.downloadURI);
        }
      } else if (book && book.pdfURI === 'nonBook' && book.downloadURI) {
        /*
         * Launch non-books
         */
        openWindow(book.downloadURI);
      } else if (book && book.type === 'Media' && book.downloadURI) {
        /*
         * Launch media
         */
        openWindow(book.downloadURI);
      } else if (book && cloud) {
        /*
         * Launch Reader for everything else
         */
        history.push(`/read/${cloud.id}/${book.id}`);
      }
    },
    [dispatch, user.accessToken]
  );
  return (
    <ConnectedCloudsAndContent
      user={user}
      onGotoContent={() => history.push('/content')}
      onBookClick={handleBookClicked}
      refreshing={refreshing}
      onRefresh={onRefresh}
      onLogout={() => logout()}
    />
  );
}

export default CloudsAndContentPage;
